import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { change, clearFields, formValueSelector, reduxForm } from 'redux-form'
// components
import { Button, BUTTON_LAYOUTS } from 'components/UI'
import { ReduxEntitySelect, ReduxInput, ReduxSelect, ReduxSwitch } from 'components/FormElements'
// view components
import LinkingSettings from 'components/Modals/RiskFormModal/components/Form/components/LinkingSettings'
// constants
import { Forms } from 'constants/index'
import { NO_VALUE_FIELD_TYPES } from 'constants/objects'
// icons
import { EditIcon } from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/Property/view'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// graphql
import { RISK_TEMPLATE_SELECT_QUERY } from 'graphql/risk/queries'
// types
import { IOption } from 'components/FormElements/Select'
import { RiskViewFormData } from 'components/Modals/RiskViewModal/components/Form'
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
import { IField, IFieldLinkSettings, IRiskTemplate } from 'types/graphql'
// views
import { FieldsSection, Form, Link } from './views'
// utils
import { required } from 'utils/index'
import { safeGetOr } from 'utils/fp'
import { map, pipe } from 'lodash/fp'
import { get, sortBy } from 'lodash'

const formSelector = formValueSelector(Forms.RISK_TEMPLATE_FORM_ADD_FORM)

type Option = {
  label: string
  value: string
  isDisabled?: boolean
}

export type FormData = {
  groupByCurrent: boolean
  groupBy: Option[]
  sourceTemplate: string
  primaryItem: Option
  fieldLabel: string
  addFieldLabel: string
  type?: string
  multiple: boolean
  riskView?: RiskViewFormData
  canLink?: boolean
  linkingSettings?: Omit<IFieldLinkSettings, 'excludeFieldsWhenCopying'> & { excludeFieldsWhenCopying: IOption[] }
  riskViewId?: string
}

interface Props {
  field: IField
  linked: boolean
  isOpen: boolean
  parentId: string
  onSubmit: () => void
}

const AddFormFieldForm: React.FC<Props & InjectedFormProps<FormData>> = ({
  error,
  field,
  linked,
  onSubmit,
  parentId,
  submitting,
  handleSubmit
}) => {
  const dispatch = useDispatch()

  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { sourceTemplate, groupByCurrent, multiple } = useSelector((state) => ({
    sourceTemplate: formSelector(state, 'sourceTemplate'),
    groupByCurrent: formSelector(state, 'groupByCurrent'),
    multiple: formSelector(state, 'multiple')
  }))

  // methods
  const onSourceTemplateChange = () =>
    dispatch(clearFields(Forms.RISK_TEMPLATE_FORM_ADD_FORM, true, true, 'primaryItem'))

  const onMultipleChange = () => dispatch(clearFields(Forms.RISK_TEMPLATE_FORM_ADD_FORM, false, false, 'groupBy'))

  // constants
  const formsOptions: Option[] = React.useMemo(
    () =>
      sortBy(riskTemplate.fieldLinks, 'position')
        .filter(({ field: { id }, primary, parentFieldId }) => {
          if (multiple) {
            return (
              parentFieldId === parentId &&
              riskTemplate.fieldGroups.some(
                ({ fieldId, groupedByEntityId, parentFieldId }) =>
                  fieldId === id && groupedByEntityId === riskTemplate.id && parentFieldId === parentId
              )
            )
          }
          return (
            primary &&
            parentFieldId === parentId &&
            riskTemplate.fieldGroups.filter(
              ({ fieldId, groupedByEntityId, parentFieldId }) =>
                fieldId === id && groupedByEntityId !== riskTemplate.id && parentFieldId === parentId
            ).length < 2
          )
        })
        .map(({ fieldLabel, field: { entityId } }) => ({
          label: fieldLabel,
          value: entityId
        })),
    [riskTemplate, multiple]
  )

  const getTemplatesOptions = pipe(
    safeGetOr<IRiskTemplate, any, IRiskTemplate[]>([], 'data.riskTemplates.data'),
    map<IRiskTemplate, Option>(({ id, name, archived }) => {
      const isAlreadyAdded = riskTemplate.fieldLinks.some(
        ({ field: { entityId }, parentFieldId }) => parentFieldId === parentId && entityId === id
      )

      return { label: name, value: id, isDisabled: id === riskTemplate.id || isAlreadyAdded || archived }
    })
  )

  const getFieldsOptions = (data) => {
    const fields: IField[] = get(data, 'data.riskTemplates.data[0].fields', [])

    return fields
      .filter((field) => field.active && !NO_VALUE_FIELD_TYPES.includes(field.fieldType))
      .map(({ id, label }) => ({
        label,
        value: id
      }))
  }

  const onPrimaryItemSelect = (option: IOption) => {
    if (option) {
      dispatch(change(Forms.RISK_TEMPLATE_FORM_ADD_FORM, 'fieldLabel', option.label))
      dispatch(change(Forms.RISK_TEMPLATE_FORM_ADD_FORM, 'addFieldLabel', option.label))
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <ReduxSwitch label='Group by current template' name='groupByCurrent' disabled={!!field} reverse />
      <ReduxSwitch
        reverse
        name='multiple'
        label='Multiple grouping'
        listenOnChange={onMultipleChange}
        disabled={!!field || groupByCurrent}
      />
      {!groupByCurrent && (
        <ReduxSelect
          name='groupBy'
          validate={required}
          isDisabled={!!field}
          isMultiple={multiple}
          options={formsOptions}
          label='Group by forms'
        />
      )}
      <ReduxInput label='Type' name='type' disabled />
      <ReduxEntitySelect
        skip={!!field}
        label='Form'
        validate={required}
        isDisabled={!!field}
        name='sourceTemplate'
        getOptions={getTemplatesOptions}
        query={RISK_TEMPLATE_SELECT_QUERY}
        onSelectChange={onSourceTemplateChange}
        handleSearch={({ search, refetch }) => refetch({ filter: { name: search } })}
      />
      <FieldsSection>
        <ReduxEntitySelect
          key={sourceTemplate?.value}
          name='primaryItem'
          validate={required}
          label='Primary item'
          getOptions={getFieldsOptions}
          skip={!sourceTemplate || !!field}
          query={RISK_TEMPLATE_SELECT_QUERY}
          onSelectChange={onPrimaryItemSelect}
          isDisabled={!!field || !sourceTemplate}
          variables={{ filter: { ids: [get(sourceTemplate, 'value')] } }}
        />
        <ReduxInput label='Label' name='fieldLabel' />
      </FieldsSection>

      {sourceTemplate && (
        <Link target='_blank' href={`/forms/templates/edit/${sourceTemplate.value}`}>
          <EditIcon />
          {`  Edit form`}
        </Link>
      )}

      <ReduxInput label='New form record function label' name='addFieldLabel' />

      {linked && <LinkingSettings error={error} />}

      <Button disabled={submitting} shadowLess type='submit' layout={BUTTON_LAYOUTS.PURPLE}>
        Done
      </Button>
    </Form>
  )
}

export default reduxForm<any, any>({
  form: Forms.RISK_TEMPLATE_FORM_ADD_FORM,
  enableReinitialize: true,
  destroyOnUnmount: true
})(AddFormFieldForm)
